<script setup lang="ts">
import { GpDropdown, GpTable, GpTableColumn, type GpTableDataRow } from '@gportal/uikit';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import ComponentStatus from 'gpcore-uikit/components/Component.Status.vue';
import { inject, onMounted, ref } from 'vue';
import type { BillStatus } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { BillingProfileInject } from '../symbols';
import useBillStatus from './composables/useBillStatus';
import useIssuedAtDate from './composables/useIssuedAtDate';

const ROWS_PER_PAGE = 5;

interface BillsTableRow extends GpTableDataRow {
  id: string;
  number: string;
  status: BillStatus;
  issuedAt: string;
  totalFormatted: string;
}

const billingprofile = inject(BillingProfileInject);

const data = ref<Array<BillsTableRow>>([]);
const page = ref(0);
const maxPages = ref(0);

const currentYear = new Date().getFullYear();
const filterYear = ref(currentYear);
const filterYears = ref<Array<number>>([]);

const isLoading = ref(false);
const hasError = ref(false);
const errorMessage = ref('');

const formatDate = useIssuedAtDate();
const userStore = useUserStore();
const reload = async () => {
  isLoading.value = true;
  try {
    const response = await userStore.reloadBillingProfileBills({
      page: page.value + 1,
      itemsPerPage: ROWS_PER_PAGE,
      year: filterYear.value,
      id: billingprofile.value.id,
    });

    data.value = response.bills.map((b) => ({
      key: b.id,
      id: b.id,
      number: b.number,
      status: b.status,
      issuedAt: formatDate(b.issuedAt),
      totalFormatted: b.totalPrice.formatted,
    }));

    filterYears.value = response.years;
    maxPages.value = response.pagination.total;
  } catch (error) {
    hasError.value = true;
    errorMessage.value = (error as ServiceError).message;
  } finally {
    isLoading.value = false;
  }
};

const handlePagination = (newPage) => {
  page.value = newPage;
  reload();
};

const filterByYear = (year: number) => {
  if (year !== filterYear.value) {
    filterYear.value = year as number;
    reload();
  }
};

const { getStatus } = useBillStatus();

onMounted(async () => {
  reload();
});
</script>

<template>
  <div>
    <div class="tw-mb-4 tw-flex tw-items-center">
      <p class="tw-mb-0 tw-w-1/2 tw-font-bold lg:tw-w-2/3">
        <template v-if="filterYear != null">Bills from {{ filterYear }}</template>
        <template v-else>All bills</template>
      </p>
      <div class="tw-w-1/2 lg:tw-w-1/3">
        <GpDropdown
          :model-value="filterYear"
          :data="filterYears"
          input-id="profile"
          placeholder="Filter by year"
          @update:model-value="filterByYear"
        />
      </div>
    </div>
    <GpTable
      :data="data"
      pagination
      :page="page"
      :max-pages="maxPages"
      :rows-per-page="ROWS_PER_PAGE"
      :is-loading="isLoading"
      loading-message="Loading bills ..."
      empty-message="No bills found."
      @update:page="handlePagination"
    >
      <GpTableColumn label="Status" field="status" header-class="tw-w-[10rem]">
        <template #body="{ data }: { data: BillsTableRow['status'] }">
          <ComponentStatus v-bind="getStatus(data)" />
        </template>
      </GpTableColumn>
      <GpTableColumn label="ID" field="number">
        <template #body="{ data, row }: { data: BillsTableRow['number']; row: BillsTableRow }">
          <RouterLink
            :to="{
              name: 'BillDetail',
              params: { billingProfileId: billingprofile.id, billId: row.id },
            }"
            >{{ data }}</RouterLink
          >
        </template>
      </GpTableColumn>
      <GpTableColumn label="Issued At" field="issuedAt" alignment="right" />
      <GpTableColumn label="Total" field="totalFormatted" alignment="right" />
    </GpTable>
  </div>
</template>

<style scoped></style>
